import React, { useEffect, useState, useCallback } from 'react';
import { Search, ArrowDownUp, X } from 'lucide-react';
import api from '../lib/api';
import { LessonCard } from '../components/LessonCard';
import { Input } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';
import { cn } from '../lib/utils';

const DIFFICULTIES = ['All', 'Beginner', 'Intermediate', 'Advanced'];

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={cn(
        'shrink-0 rounded-full border px-4 py-1.5 text-sm transition-colors',
        active
          ? 'border-white/0 bg-white text-black'
          : 'border-white/10 text-zinc-400 hover:border-white/30 hover:text-white'
      )}
    >
      {children}
    </button>
  );
}

export default function Lessons() {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [difficulty, setDifficulty] = useState('All');
  const [topic, setTopic] = useState('All');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('newest');
  const [topics, setTopics] = useState(['All']);

  // Real production taxonomy: build topic filters from /tags (fallback to /topics-with-counts).
  useEffect(() => {
    api
      .get('/tags')
      .then(({ data }) => {
        const names = (data.tags || []).map((t) => t.name).filter(Boolean);
        if (names.length) setTopics(['All', ...names]);
      })
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { sort };
    if (difficulty !== 'All') params.difficulty = difficulty;
    if (topic !== 'All') params.topic = topic;
    if (q) params.q = q;
    api
      .get('/lessons', { params })
      .then(({ data }) => setLessons(data.lessons || []))
      .finally(() => setLoading(false));
  }, [difficulty, topic, q, sort]);

  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-white">Lessons</h1>
          <p className="mt-1 text-sm text-zinc-500">Browse the full curriculum.</p>
        </div>
        <div className="flex w-full flex-col gap-3 sm:flex-row md:w-auto">
          <div className="relative w-full sm:w-80">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              placeholder="Search lessons…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-10"
              data-testid="lessons-search-input"
            />
          </div>
          <div className="relative shrink-0">
            <ArrowDownUp size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="lessons-sort-select"
              className="h-11 w-full rounded-xl border border-zinc-800 bg-ink-surface pl-9 pr-8 text-sm text-white outline-none focus:border-zinc-600 sm:w-48"
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="recommended">Recommended</option>
            </select>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <span className="w-14 shrink-0 text-xs font-semibold uppercase tracking-wide text-zinc-500">Level</span>
          <div className="no-scrollbar flex gap-2 overflow-x-auto">
            {DIFFICULTIES.map((d) => (
              <Chip key={d} active={difficulty === d} onClick={() => setDifficulty(d)} testid={`filter-difficulty-${d}`}>
                {d}
              </Chip>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="w-14 shrink-0 text-xs font-semibold uppercase tracking-wide text-zinc-500">Tag</span>
          <div className="no-scrollbar flex gap-2 overflow-x-auto">
            {topics.map((t) => (
              <Chip key={t} active={topic === t} onClick={() => setTopic(t)} testid={`filter-topic-${t}`}>
                {t}
              </Chip>
            ))}
          </div>
        </div>
        {(difficulty !== 'All' || topic !== 'All' || q) && (
          <button
            onClick={() => { setDifficulty('All'); setTopic('All'); setQ(''); }}
            data-testid="lessons-clear-filters"
            className="flex items-center gap-1.5 text-xs text-zinc-500 transition-colors hover:text-emerald"
          >
            <X size={13} /> Clear filters
          </button>
        )}
      </div>

      {loading ? (
        <div className="grid h-[40vh] place-items-center">
          <Spinner className="h-8 w-8" />
        </div>
      ) : lessons.length ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {lessons.map((l, i) => (
            <LessonCard key={l.lesson_id} lesson={l} index={i} />
          ))}
        </div>
      ) : (
        <div className="grain relative rounded-3xl border border-white/5 bg-ink-card p-16 text-center">
          <p className="font-heading text-lg text-white">No lessons match your filters</p>
          <p className="mt-2 text-sm text-zinc-500">Try clearing the search or picking a different topic.</p>
        </div>
      )}
    </div>
  );
}
